import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { BLOCKED_BY_LABELS } from "@/hooks/use-audits";

interface BlockAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string, blockedBy: "project_team" | "compliance_team") => Promise<void>;
  isPending: boolean;
}

const schema = z.object({
  reason: z.string().trim().min(1, "A reason is required to mark this audit as blocked"),
  blockedBy: z.enum(["project_team", "compliance_team"], {
    message: "Select who this audit is blocked by",
  }),
});

type FormValues = z.infer<typeof schema>;

export function BlockAuditDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: BlockAuditDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: "", blockedBy: undefined },
  });

  useEffect(() => {
    if (open) form.reset({ reason: "", blockedBy: undefined });
  }, [open, form]);

  async function onSubmit(values: FormValues) {
    await onConfirm(values.reason, values.blockedBy);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Block Audit</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="blockedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Blocked By</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select who this audit is blocked by" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(BLOCKED_BY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Explain why this audit is being blocked…"
                      rows={4}
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={isPending}>
                {isPending ? "Blocking…" : "Block Audit"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
